(() => {
  const selector = '.dh-catalog-card .card__media img, .collection-card-wrapper .card__media img, .category-tile img, .product__media img, .thumbnail img, .cart-item__image';
  const results = new Map();
  const observed = new WeakSet();
  const idle = callback => 'requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 700 }) : setTimeout(callback, 0);

  function makeMask(image) {
    const scale = Math.min(1, 800 / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.round(image.naturalWidth * scale);
    const height = Math.round(image.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height);
    const data = pixels.data;
    const samples = [];
    for (let step = 0; step < 60; step++) {
      const x = Math.round(step * (width - 1) / 59);
      const y = Math.round(step * (height - 1) / 59);
      for (const index of [x, (height - 1) * width + x, y * width, y * width + width - 1]) {
        const offset = index * 4;
        if (data[offset + 3] < 250) return null;
        samples.push([data[offset], data[offset + 1], data[offset + 2]]);
      }
    }
    const background = [0, 1, 2].map(channel => samples.map(pixel => pixel[channel]).sort((a, b) => a - b)[120]);
    if (Math.min(...background) < 190 || Math.max(...background) - Math.min(...background) > 16) return null;
    const similar = pixel => Math.max(...pixel.map((value, channel) => Math.abs(value - background[channel]))) < 22;
    if (samples.filter(similar).length / samples.length < .80) return null;
    const visited = new Uint8Array(width * height);
    const queue = new Int32Array(width * height);
    let start = 0;
    let end = 0;
    let removed = 0;
    const add = index => {
      if (visited[index]) return;
      visited[index] = 1;
      const offset = index * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const distance = Math.max(Math.abs(r - background[0]), Math.abs(g - background[1]), Math.abs(b - background[2]));
      if (distance > 36 || Math.max(r, g, b) - Math.min(r, g, b) > 23) return;
      if (r > background[0] + 10 || g > background[1] + 10 || b > background[2] + 10) return;
      if (distance < 16) queue[end++] = index;
      data[offset + 3] = Math.round(255 * Math.min(1, Math.max(0, (distance - 5) / 20)));
      removed++;
    };
    for (let x = 0; x < width; x++) { add(x); add((height - 1) * width + x); }
    for (let y = 0; y < height; y++) { add(y * width); add(y * width + width - 1); }
    while (start < end) {
      const index = queue[start++];
      const x = index % width;
      if (x > 0) add(index - 1);
      if (x < width - 1) add(index + 1);
      if (index >= width) add(index - width);
      if (index < width * (height - 1)) add(index + width);
    }
    if (removed < width * height * .15 || removed > width * height * .94) return null;
    for (let offset = 0; offset < data.length; offset += 4) { data[offset] = 255; data[offset + 1] = 255; data[offset + 2] = 255; }
    context.putImageData(pixels, 0, 0);
    return new Promise(resolve => canvas.toBlob(blob => resolve(blob ? URL.createObjectURL(blob) : null), 'image/png'));
  }

  function loadMask(source) {
    if (results.has(source)) return results.get(source);
    const result = new Promise(resolve => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => idle(async () => {
        try { resolve(await makeMask(image)); } catch { resolve(null); }
      });
      image.onerror = () => resolve(null);
      image.src = source;
    });
    results.set(source, result);
    return result;
  }

  async function isolate(image) {
    const source = image.currentSrc || image.src;
    if (!source || !image.complete || !image.naturalWidth || image.dataset.dhMaskSource === source) return;
    image.dataset.dhMaskSource = source;
    const mask = await loadMask(source);
    if (!image.isConnected || (image.currentSrc || image.src) !== source) return;
    if (mask) {
      image.style.maskImage = `url("${mask}")`;
      image.style.webkitMaskImage = `url("${mask}")`;
      image.classList.add('dh-isolated-image');
      image.dataset.dhMedia = 'isolated';
    } else {
      image.style.maskImage = '';
      image.style.webkitMaskImage = '';
      image.classList.remove('dh-isolated-image');
      image.dataset.dhMedia = 'original';
    }
  }

  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) isolate(entry.target);
  }, { rootMargin: '200px' });

  function setup(root = document) {
    root.querySelectorAll(selector).forEach(image => {
      if (observed.has(image)) return;
      observed.add(image);
      observer.observe(image);
      image.addEventListener('load', () => isolate(image));
    });
  }
  setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node instanceof Element) setup(node);
  }).observe(document.body, { childList: true, subtree: true });
})();
